import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Gift, TrendUp, Briefcase, Phone, EnvelopeSimple, WhatsappLogo, Info, Globe } from "@phosphor-icons/react";
import { Logo, NEUTRAL_NAME } from "../components/Logo";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Landing() {
  const navigate = useNavigate();
  const { refCode } = useParams();
  const [sp] = useSearchParams();
  const pmode = sp.get("pmode");
  const pShort = sp.get("p");
  const praemieOverride = (pShort === "0" || pmode === "aus") ? false : null;
  const { user } = useAuth();

  useEffect(() => {
    if (!refCode && user) {
      navigate(user.rolle === "berater" ? "/berater" : "/app", { replace: true });
    }
  }, [user, refCode, navigate]);

  const [advisorName, setAdvisorName] = useState(null);
  const [inaktiv, setInaktiv] = useState(false);
  const [advisorFirma, setAdvisorFirma] = useState(null);
  const [advisorZusatz, setAdvisorZusatz] = useState("");
  const [advisorBrancheZusatz, setAdvisorBrancheZusatz] = useState("Vermögensberatung");
  const [advisorBrancheBeruf, setAdvisorBrancheBeruf] = useState("Vermögensberater:in");
  const [advisorBrancheNutzen, setAdvisorBrancheNutzen] = useState("einer ehrlichen, professionellen Finanz- und Vermögensberatung");
  const [advisorPraemieAktiv, setAdvisorPraemieAktiv] = useState(true);
  const [advisorPraemieTyp, setAdvisorPraemieTyp] = useState("geld");
  const [advisorPraemieSache, setAdvisorPraemieSache] = useState("");
  const [advisorSlogan, setAdvisorSlogan] = useState(null);
  const [advisorClaim, setAdvisorClaim] = useState("");
  const [advisorSubtext, setAdvisorSubtext] = useState(null);
  const [advisorTelefon, setAdvisorTelefon] = useState("");
  const [advisorEmail, setAdvisorEmail] = useState("");
  const [advisorDatenschutzUrl, setAdvisorDatenschutzUrl] = useState("");
  const [advisorLogo, setAdvisorLogo] = useState(null);
  const [advisorPortrait, setAdvisorPortrait] = useState(null);
  const [advisorWhatsapp, setAdvisorWhatsapp] = useState("");
  const [advisorWebseite, setAdvisorWebseite] = useState("");
  const [advisorKarriere, setAdvisorKarriere] = useState(true);
  const [advisorKarriereText, setAdvisorKarriereText] = useState("");
  const [advisorPersonalLogo, setAdvisorPersonalLogo] = useState(null);
  const [brandingPos, setBrandingPos] = useState(null);
  const [advisorKontaktReihenfolge, setAdvisorKontaktReihenfolge] = useState(["telefon", "whatsapp", "email", "webseite"]);
  useEffect(() => {
    if (refCode) {
      api.get(`/berater/public/${refCode}`).then(({ data }) => {
        if (data.inaktiv) {
          if (data.redirect) { window.location.href = data.redirect; return; }
          setInaktiv(true);
        }
        if (data.name) setAdvisorName(data.name);
        setAdvisorFirma(data.firma || null);
        setAdvisorZusatz(data.zusatz || "");
        if (data.branche_zusatz) setAdvisorBrancheZusatz(data.branche_zusatz);
        if (data.branche_beruf) setAdvisorBrancheBeruf(data.branche_beruf);
        if (data.branche_nutzen) setAdvisorBrancheNutzen(data.branche_nutzen);
        setAdvisorPraemieAktiv(praemieOverride !== null ? praemieOverride : (data.praemie_aktiv !== false));
        setAdvisorSlogan(data.slogan || null);
        setAdvisorClaim(data.claim || "");
        setAdvisorSubtext(data.subtext || null);
        setAdvisorTelefon(data.telefon || "");
        setAdvisorEmail(data.email || "");
        setAdvisorDatenschutzUrl(data.datenschutz_url || "");
        setAdvisorLogo(data.logo_url || null);
        setAdvisorPortrait(data.portrait_url || null);
        setAdvisorWhatsapp(data.whatsapp || "");
        setAdvisorWebseite(data.webseite || "");
        if (typeof data.karriere_aktiv === "boolean") setAdvisorKarriere(data.karriere_aktiv);
        if (data.karriere_text) setAdvisorKarriereText(data.karriere_text);
        setAdvisorPersonalLogo(data.personal_logo_url || null);
        setAdvisorPraemieTyp(data.praemie_typ || "geld");
        setAdvisorPraemieSache(data.praemie_sache || "");
        setBrandingPos(data.branding_pos || null);
        if (Array.isArray(data.kontakt_reihenfolge) && data.kontakt_reihenfolge.length) setAdvisorKontaktReihenfolge(data.kontakt_reihenfolge);
      }).catch(() => {});
    }
  }, [refCode]);  // eslint-disable-line react-hooks/exhaustive-deps

  const initials = (advisorName || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  const QUOTE = "Eine Empfehlung ist das höchste Gut – der wahre Unternehmerlohn.";
  const norm = (s) => (s || "").toLowerCase().replace(/[„""«».,–—-]/g, "").replace(/\s+/g, " ").trim();
  const showQuote = ![advisorSlogan, advisorSubtext, advisorClaim].some((s) => norm(s) === norm(QUOTE));

  const _ss = (brandingPos && brandingPos.startseite) || {};
  const _elUrl = { firmenlogo: advisorLogo, persoenlich: advisorPersonalLogo, portrait: advisorPortrait };
  const _headerBadge = (slot) => {
    const el = ["firmenlogo", "persoenlich", "portrait"].find((e) => _ss[e] === slot && _elUrl[e]);
    if (!el) return null;
    const cls = el === "portrait"
      ? "h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-[#C5A059] shadow-[0_0_0_5px_rgba(197,160,89,0.14)]"
      : "h-12 w-12 shrink-0 rounded-full bg-white object-contain p-1 shadow-[0_0_0_5px_rgba(197,160,89,0.14)] ring-2 ring-[#C5A059]";
    return <img src={_elUrl[el]} alt={el === "portrait" ? "Beraterfoto" : "Logo"} data-testid={`header-${slot}`} className={cls} />;
  };
  const _cardEl = ["firmenlogo", "persoenlich", "portrait"].find((e) => _ss[e] === "karte" && _elUrl[e]);

  const kontaktOrder = advisorKontaktReihenfolge && advisorKontaktReihenfolge.length ? advisorKontaktReihenfolge : ["telefon", "whatsapp", "email", "webseite"];
  const contactBtn = {
    telefon: advisorTelefon ? <a key="telefon" href={`tel:${advisorTelefon.replace(/\s/g, "")}`} data-testid="advisor-call" aria-label="Berater anrufen" className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#22C55E] text-white shadow-sm active:scale-95"><Phone size={16} weight="fill" /></a> : null,
    whatsapp: advisorWhatsapp ? <a key="whatsapp" href={`https://wa.me/${advisorWhatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" data-testid="advisor-whatsapp" aria-label="WhatsApp" className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#25D366] text-white shadow-sm active:scale-95"><WhatsappLogo size={16} weight="fill" /></a> : null,
    email: advisorEmail ? <a key="email" href={`mailto:${advisorEmail}`} data-testid="advisor-mail" aria-label="Berater schreiben" className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#C5A059] text-[#0A1128] shadow-sm active:scale-95"><EnvelopeSimple size={16} weight="fill" /></a> : null,
    webseite: advisorWebseite ? <a key="webseite" href={advisorWebseite} target="_blank" rel="noreferrer" data-testid="advisor-webseite" aria-label="Webseite" className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white shadow-sm active:scale-95"><Globe size={16} weight="fill" /></a> : null,
  };

  const effectiveClaim = advisorClaim || (advisorPraemieAktiv === false ? "" : "Empfehlen & Profitieren");

  const start = () => {
    if (user && user.rolle === "berater") return navigate("/berater");
    if (user && user.rolle === "kunde") return navigate("/app");
    navigate(refCode ? `/empfehlen?ref=${refCode}${pmode ? `&pmode=${pmode}` : ""}` : "/empfehlen");
  };

  const startKarriere = () =>
    navigate(refCode ? `/empfehlen?ref=${refCode}&modus=karriere` : "/empfehlen?modus=karriere");

  if (inaktiv) {
    return (
      <div className="relative mx-auto flex min-h-screen max-w-md flex-col items-center justify-center overflow-hidden bg-[#0A1128] px-8 text-center text-white" data-testid="inaktiv-page">
        <div className="pointer-events-none absolute -right-16 top-24 h-56 w-56 rounded-full bg-[#C5A059]/20 blur-3xl" />
        <div className="relative">
          <div className="mb-6 flex justify-center"><Logo light /></div>
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[#C5A059]/40 bg-white/[0.06]">
            <Info size={30} weight="duotone" color="#C5A059" />
          </div>
          <h1 className="font-display text-2xl font-light tracking-tight">Dieser Empfehlungs-Link ist zurzeit nicht aktiv</h1>
          <p className="mx-auto mt-4 max-w-xs text-sm leading-relaxed text-white/70">
            Der Link ist momentan pausiert. Bitte wende dich direkt an die Person, von der du ihn erhalten hast – sie hilft dir gerne weiter.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto min-h-screen max-w-md overflow-hidden bg-[#0A1128] text-white">
      {/* ambient texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1640654694019-b9d3567c0e56?crop=entropy&cs=srgb&fm=jpg&q=85&w=800')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="pointer-events-none absolute -right-16 top-20 h-56 w-56 rounded-full bg-[#C5A059]/20 blur-3xl" />

      <div className="relative flex min-h-screen flex-col px-7 pb-10 pt-[calc(env(safe-area-inset-top)+2rem)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo light {...(advisorPraemieAktiv === false ? { name: NEUTRAL_NAME, claim: effectiveClaim } : { claim: effectiveClaim })} />
            {_headerBadge("links")}
          </div>
          {_headerBadge("rechts")}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-20 flex-1"
        >
          {advisorName ? (
            <div className="mb-6 flex items-center gap-3.5 rounded-2xl border border-[#C5A059]/40 bg-white/[0.06] p-4 backdrop-blur-sm" data-testid="advisor-card">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#C5A059] text-base font-extrabold text-[#0A1128]" data-testid="advisor-avatar">
                {_cardEl ? (
                  <img src={_elUrl[_cardEl]} alt={_cardEl === "portrait" ? "Beraterfoto" : "Logo"} className={_cardEl === "portrait" ? "h-full w-full object-cover" : "h-full w-full bg-white object-contain p-1"} />
                ) : (
                  <span>{initials || "★"}</span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#C5A059]">Deine Empfehlung geht an</p>
                <p className="whitespace-pre-line text-lg font-bold leading-tight text-white line-clamp-2" data-testid="advisor-name">{advisorName}</p>
                {advisorZusatz ? <p className="whitespace-pre-line text-xs font-medium leading-tight text-[#C5A059]" data-testid="advisor-zusatz">{advisorZusatz}</p> : null}
                {advisorFirma ? <p className="truncate text-xs text-white/60">{advisorFirma}</p> : null}
                {advisorPraemieAktiv !== false && advisorPraemieTyp === "sache" && advisorPraemieSache ? (
                  <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#C5A059]/15 px-2.5 py-0.5 text-[11px] font-semibold text-[#C5A059]" data-testid="advisor-sachpraemie">🎁 Dankeschön: {advisorPraemieSache}</p>
                ) : null}
                {(advisorTelefon || advisorEmail || advisorWhatsapp || advisorWebseite) ? (
                  <motion.div initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 0.4, ease: "easeOut" }} className="mt-2 flex items-center gap-2">
                    {kontaktOrder.map((k) => contactBtn[k])}
                  </motion.div>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="mb-5 h-px w-12 bg-[#C5A059]" />
          {effectiveClaim ? (
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059]" data-testid="eyebrow-claim">{effectiveClaim}</p>
          ) : null}
          <h1 className="font-display text-4xl sm:text-5xl font-light leading-[1.1] tracking-tight break-words">
            {advisorPraemieAktiv === false
              ? (advisorSlogan || "Empfehlen aus Überzeugung.")
              : (advisorSlogan || "Empfehlen Sie uns weiter – wir bedanken uns.")}
          </h1>
          {advisorPraemieAktiv === false ? (
            <>
              <p className="mt-5 max-w-sm text-base leading-relaxed text-white/70">
                {advisorSubtext || `Kennst du jemanden, der von ${advisorBrancheNutzen} profitiert? Deine Empfehlung hilft weiter – ganz ohne Prämienversprechen.`}
              </p>
              <div className="mt-10 space-y-4">
                <Feature icon={<ShieldCheck size={20} weight="duotone" color="#C5A059" />} title="Aus Überzeugung empfehlen" text="Weil gute Beratung Weiterempfehlung verdient." />
                <Feature icon={<TrendUp size={20} weight="duotone" color="#C5A059" />} title="Fortschritt live verfolgen" text="Von der Einreichung bis zum Abschluss – transparent." />
                <Feature icon={<ShieldCheck size={20} weight="duotone" color="#C5A059" />} title="DSGVO-konform" text="Nur mit Zustimmung der empfohlenen Person." />
              </div>
            </>
          ) : (
            <>
              <p className="mt-5 max-w-sm text-base leading-relaxed text-white/70">
                {advisorSubtext || `Empfehlen Sie eine Person, die von ${advisorBrancheNutzen} profitiert. Verfolgen Sie den Fortschritt live und sichern Sie sich Ihre Prämie.`}
              </p>
              <div className="mt-10 space-y-4">
                <Feature icon={<Gift size={20} weight="duotone" color="#C5A059" />} title="Attraktive Geldprämie" text="Für jeden geworbenen Kunden – ausgezahlt auf Ihr Konto." />
                <Feature icon={<TrendUp size={20} weight="duotone" color="#C5A059" />} title="Fortschritt live verfolgen" text="Von der Einreichung bis zum Abschluss – transparent." />
                <Feature icon={<ShieldCheck size={20} weight="duotone" color="#C5A059" />} title="DSGVO-konform" text="Nur mit Zustimmung der empfohlenen Person." />
              </div>
            </>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-10"
        >
          <button
            data-testid="start-btn"
            onClick={start}
            className="group flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#C5A059] text-base font-semibold text-[#0A1128] shadow-[0_8px_30px_rgba(197,160,89,0.35)] transition-all duration-200 active:scale-[0.98]"
          >
            Kontakt empfehlen
            <ArrowRight size={20} weight="bold" className="transition-transform group-hover:translate-x-1" />
          </button>

          {advisorKarriere && (
          <div className="my-5 flex items-center gap-3" data-testid="landing-divider">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/40">oder</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          )}

          {advisorKarriere && (<>
          <button
            data-testid="career-hint-btn"
            onClick={startKarriere}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 py-3 text-sm font-medium text-white/80 transition-colors hover:bg-white/10"
          >
            <Briefcase size={17} weight="duotone" color="#C5A059" /> {advisorKarriereText || "Interesse am Beraterberuf?"}
          </button>
          <p className="mt-3 text-center text-sm leading-relaxed text-white/70" data-testid="career-sentence">
            Nichts für dich? Vielleicht kennst du jemanden, für den das spannend ist. Der Beruf {advisorBrancheBeruf} bietet flexible Arbeitszeiten, unternehmerische Freiheit und echtes Einkommenspotenzial – bei uns in Voll- oder Teilzeit, auch nebenberuflich als zweites Standbein, mit schrittweiser Einarbeitung an deiner Seite.
          </p>
          </>)}
        </motion.div>

        {showQuote && (
          <div className="mt-12 flex flex-col items-center px-2 text-center" data-testid="unternehmerlohn-quote">
            <span className="font-display text-5xl leading-none text-[#C5A059]/40" aria-hidden="true">”</span>
            <p className="-mt-3 max-w-[17rem] text-sm font-medium italic leading-relaxed text-white/65">
              {QUOTE}
            </p>
          </div>
        )}

        <div className="mt-32 border-t-2 border-white/20 pt-12 text-center">
          <button data-testid="advisor-login-link" onClick={() => navigate("/login")} className="mx-auto inline-flex items-center gap-1.5 rounded-full border border-[#C5A059]/60 bg-[#C5A059]/15 px-4 py-1.5 text-xs font-semibold text-[#C5A059] shadow-sm backdrop-blur-sm transition-colors hover:bg-[#C5A059]/25 active:scale-95">
            <Briefcase size={13} weight="fill" /> Berater-Login
          </button>

          <div className="mt-12 border-t-2 border-white/20 pt-6">
            <p className="mx-auto mb-3 max-w-xs text-xs leading-relaxed text-white/50" data-testid="datenschutz-hinweis">
              Deine Daten werden DSGVO-konform verarbeitet und nur nach ausdrücklicher Bestätigung weitergegeben. Ohne Bestätigung erfolgt keine Kontaktaufnahme.
            </p>
            <div className="flex items-center justify-center gap-3 text-xs text-white/30">
              {advisorDatenschutzUrl ? (
                <a href={advisorDatenschutzUrl} target="_blank" rel="noreferrer" data-testid="footer-datenschutz" className="transition-colors hover:text-white/60">Datenschutz</a>
              ) : (
                <button data-testid="footer-datenschutz" onClick={() => navigate("/datenschutz")} className="transition-colors hover:text-white/60">Datenschutz</button>
              )}
              <span className="text-white/15">·</span>
              <button data-testid="footer-impressum" onClick={() => navigate("/impressum")} className="transition-colors hover:text-white/60">Impressum</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        <p className="text-sm text-white/50">{text}</p>
      </div>
    </div>
  );
}
